<template>
  <el-row class="wrapper flex-fill px-3 px-md-5 mt-4 overflow-y-auto">
    <el-col :sm="{ span: 12, offset: 6 }" :md="{ span: 12, offset: 6 }" :lg="{ span: 10, offset: 7 }" :xl="{ span: 10, offset: 7 }">
      <div class="d-flex flex-column gap-3 justify-content-center py-4">
        <div class="d-flex mb-4 align-items-center justify-content-center logo">
          <img id="logo" :src="userStore.domain?.logo ?? defaultLogoPathWhite" />
        </div>
        <div class="bg-white p-4">
          <div class="form-type-selection">
            <el-button :class="{ active: formType === 'company' }" @click="formType = 'company'">
              {{ $t('components.login.company') }}
            </el-button>
            <el-button :class="{ active: formType === 'private' }" @click="formType = 'private'">
              {{ $t('components.login.private') }}
            </el-button>
          </div>
          <!-- Added ref for form -->
          <el-form
            ref="registerForm"
            :key="formType"
            :model="formType === 'company' ? companyData : privateData"
            label-position="top"
            require-asterisk-position="right"
            @submit.prevent="submitRegister"
          >
            <div class="flex-column">
              <!-- Company-only field -->
              <el-form-item
                v-if="formType === 'company'"
                :label="$t('components.login.companyName')"
                prop="companyName"
                required
                class="form-text"
                :rules="{ required: true, message: $t('error.vuelidate.required') }"
              >
                <el-input v-model="companyData.companyName" class="custom-input" required />
              </el-form-item>
              <div v-if="formType === 'company'" class="flex-item mb-2">
                <el-form-item
                  :label="$t('components.login.vatNumber')"
                  prop="vatNumber"
                  class="form-text"
                  :rules="{ required: false }"
                >
                  <el-input v-model="companyData.vatNumber"/>
                </el-form-item>
              </div>
              <!-- Username and Email -->
              <div class="row">
                <div class="col-md-6 flex-item mb-2">
                  <template v-if="formType === 'company'">
                    <el-form-item
                      :label="$t('components.login.username')"
                      prop="username"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-input v-model="companyData.username" required />
                    </el-form-item>
                  </template>

                  <template v-else>
                    <el-form-item
                      :label="$t('components.login.username')"
                      prop="username"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-input v-model="privateData.username" required />
                    </el-form-item>
                  </template>
                </div>
                <div class="col-md-6 flex-item mb-2">
                  <template v-if="formType === 'company'">
                    <el-form-item
                      :label="$t('components.login.email')"
                      prop="email"
                      required
                      class="form-text"
                      :rules="[
                        { required: true,
                          message: $t('error.vuelidate.required') 
                        },
                        {
                          type: 'email',
                          message: $t('error.vuelidate.email'),
                          trigger: ['blur', 'change'],
                        },
                      ]"
                    >
                      <el-input v-model="companyData.email" required />
                    </el-form-item>
                  </template>
                  <template v-else>
                    <el-form-item
                      :label="$t('components.login.email')"
                      prop="email"
                      required
                      class="form-text"
                      :rules="[
                        { required: true,
                          message: $t('error.vuelidate.required') 
                        },
                        {
                          type: 'email',
                          message: $t('error.vuelidate.email'),
                          trigger: ['blur', 'change'],
                        },
                      ]"
                    >
                      <el-input v-model="privateData.email" required />
                    </el-form-item>
                  </template>
                </div>
              </div>

              <!-- First Name and Last Name -->
              <div class="row">
                <div class="col-md-6 flex-item mb-2">
                  <template v-if="formType === 'company'">
                    <el-form-item
                      :label="$t('components.login.firstName')"
                      prop="first_name"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-input v-model="companyData.first_name" required />
                    </el-form-item>
                  </template>
                  <template v-else>
                    <el-form-item
                      :label="$t('components.login.firstName')"
                      prop="first_name"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-input v-model="privateData.first_name" required />
                    </el-form-item>
                  </template>
                </div>
                <div class="col-md-6 flex-item mb-2">
                  <template v-if="formType === 'company'">
                    <el-form-item
                      :label="$t('components.login.lastName')"
                      prop="last_name"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-input v-model="companyData.last_name" required />
                    </el-form-item>
                  </template>
                  <template v-else>
                    <el-form-item
                      :label="$t('components.login.lastName')"
                      prop="last_name"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-input v-model="privateData.last_name" required />
                    </el-form-item>
                  </template>
                </div>
              </div>

              <!-- Password and Password Repeat -->
              <div class="row">
                <div class="col-md-6 flex-item mb-2">
                  <template v-if="formType === 'company'">
                    <el-form-item
                      :label="$t('components.login.password')"
                      prop="password"
                      required
                      class="form-text"
                      :rules="[
                        { required: true, message: $t('error.vuelidate.required') },
                        { validator: validatePassword, trigger: 'blur' }
                      ]"
                    >
                      <el-input v-model="companyData.password" type="password" required />
                    </el-form-item>
                  </template>
                  <template v-else>
                    <el-form-item
                      :label="$t('components.login.password')"
                      prop="password"
                      required
                      class="form-text"
                      :rules="[
                        { required: true, message: $t('error.vuelidate.required') },
                        { validator: validatePassword, trigger: 'blur' }
                      ]"
                    >
                      <el-input v-model="privateData.password" type="password" required />
                    </el-form-item>
                  </template>
                </div>
                <div class="col-md-6 flex-item mb-2">
                  <template v-if="formType === 'company'">
                    <el-form-item
                      :label="$t('components.login.passwordRepeat')"
                      prop="passwordRepeat"
                      required
                      class="form-text"
                      :rules="[
                        { required: true, message: $t('error.vuelidate.required') },
                        { validator: validatePasswordRepeat, trigger: 'blur' }
                      ]"
                    >
                      <el-input v-model="companyData.passwordRepeat" type="password" required />
                    </el-form-item>
                  </template>
                  <template v-else>
                    <el-form-item
                      :label="$t('components.login.passwordRepeat')"
                      prop="passwordRepeat"
                      required
                      class="form-text"
                      :rules="[
                        { required: true, message: $t('error.vuelidate.required') },
                        { validator: validatePasswordRepeat, trigger: 'blur' }
                      ]"
                    >
                      <el-input v-model="privateData.passwordRepeat" type="password" required />
                    </el-form-item>
                  </template>
                </div>
              </div>

              <!-- Private-only field -->
              <div v-if="formType === 'private'" class="row">
                <h4>{{ $t('components.login.address') }}</h4>
                <div class="col-md-6 flex-item mb-2">
                  <el-form-item
                    :label="$t('components.login.street')"
                    prop="address"
                    required
                    class="form-text"
                    :rules="{ required: true, message: $t('error.vuelidate.required') }"
                  >
                    <el-input v-model="privateData.address" required />
                  </el-form-item>
                </div>
                <div class="col-md-6 flex-item mb-2">
                  <el-form-item
                    :label="$t('components.login.postalCode')"
                    prop="postalCode"
                    required
                    class="form-text"
                    :rules="{ required: true, message: $t('error.vuelidate.required') }"
                  >
                    <el-input v-model="privateData.postalCode" required />
                  </el-form-item>
                </div>
              </div>

              <!-- Company-only fields -->
              <div v-if="formType === 'company'" class="row">
                <h4>{{ $t('components.login.billingAddress') }}</h4>
                <div class="col-md-6 flex-item mb-2">

                  <el-form-item
                    :label="$t('components.login.street')"
                    prop="billingAddress"
                    required
                    class="form-text"
                    :rules="{ required: true, message: $t('error.vuelidate.required') }"
                  >
                    <el-input v-model="companyData.billingAddress" required />
                  </el-form-item>
                </div>
                <div class="col-md-6 flex-item mb-2">
                  <el-form-item
                    :label="$t('components.login.postalCode')"
                    prop="postalCode"
                    required
                    class="form-text"
                    :rules="{ required: true, message: $t('error.vuelidate.required') }"
                  >
                    <el-input v-model="companyData.postalCode" required />
                  </el-form-item>
                </div>
              </div>

              <!-- City and State/Region -->
              <div class="row">
                <div class="col-md-6 flex-item mb-2">
                  <template v-if="formType === 'company'">
                    <el-form-item
                      :label="$t('components.login.city')"
                      prop="city"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-input v-model="companyData.city" required />
                    </el-form-item>
                  </template>
                  <template v-else>
                    <el-form-item
                      :label="$t('components.login.city')"
                      prop="city"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-input v-model="privateData.city" required />
                    </el-form-item>
                  </template>
                </div>
                <div class="col-md-6 flex-item mb-2">
                  <template v-if="formType === 'company'">
                    <el-form-item
                      :label="$t('components.login.country')"
                      prop="country"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-select
                        v-model="companyData.country"
                        filterable
                        clearable
                        :placeholder="$t('components.login.countryPlaceholder')"
                      >
                        <el-option
                          v-for="c in availableCountries"
                          :key="c.name"
                          :label="$t(`enum.countries.${c.countryCode}`) || c.name"
                          :value="c.name"
                        />
                      </el-select>
                    </el-form-item>
                  </template>

                  <template v-else>
                    <el-form-item
                      :label="$t('components.login.country')"
                      prop="country"
                      required
                      class="form-text"
                      :rules="{ required: true, message: $t('error.vuelidate.required') }"
                    >
                      <el-select
                        v-model="privateData.country"
                        filterable
                        clearable
                        :placeholder="$t('components.login.countryPlaceholder')"
                      >
                        <el-option
                          v-for="c in availableCountries"
                          :key="c.name"
                          :label="$t(`components.login.countries.${c.name}`) || c.name"
                          :value="c.name"
                        />
                      </el-select>
                    </el-form-item>
                  </template>
                </div>
              </div>

              <!-- Phone Number -->
              <div class="flex-item mb-2">
                <template v-if="formType === 'company'">
                  <el-form-item
                    :label="$t('components.login.phoneNumber')"
                    prop="phoneNumber"
                    required
                    class="form-text"
                    :rules="[
                      { required: true, message: $t('error.vuelidate.required') },
                      { validator: validatePhone, trigger: ['blur', 'change'] }
                    ]"
                  >
                    <el-input v-model="companyData.phoneNumber" required />
                  </el-form-item>
                </template>
                <template v-else>
                  <el-form-item
                    :label="$t('components.login.phoneNumber')"
                    prop="phoneNumber"
                    required
                    class="form-text"
                    :rules="[
                      { required: true, message: $t('error.vuelidate.required') },
                      { validator: validatePhone, trigger: ['blur', 'change'] }
                    ]"
                  >
                    <el-input v-model="privateData.phoneNumber" required />
                  </el-form-item>
                </template>
              </div>
            </div>
            <div class="pt-3">
              <el-button class="button" type="primary" native-type="submit" @click="submitRegister">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else class="btn-register">{{ $t('components.login.signUp') }}</span>
              </el-button>
            </div>
          </el-form>
          <div class="footer">
            <p>
              {{ $t('components.login.alreadyHaveAccount') }}
              <span style="color: #0075ff; cursor: pointer" @click="navigateTo('/login')">
                {{ $t('components.login.login') }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </el-col>
  </el-row>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-facing-decorator';
import { ElForm, ElMessage, FormItemRule } from 'element-plus';
import { useRouter } from 'vue-router';
import { endpointAuthSystem } from '@/services/api/api';
import { useUserStore } from '@/stores/userStore';
import { defaultLogoPathWhite } from '@/utils/theme';
import { Country, getAllAvailableCountries } from '@/services/api/userService';

interface CompanyData {
  companyName: string;
  username: string;
  email: string;
  vatNumber: string;
  billingAddress: string;
  cekoon_private: boolean;
  postalCode: string;
  first_name: string;
  last_name: string;
  password: string;
  passwordRepeat: string;
  city: string;
  country: string;
  stateRegion: string;
  phoneNumber: string;
  urlSuffix: string;
  domainId: string;
}

interface PrivateData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  address: string;
  password: string;
  cekoon_private: boolean;
  passwordRepeat: string;
  city: string;
  country: string;
  stateRegion: string;
  phoneNumber: string;
  postalCode: string;
  urlSuffix: string;
  domainId: string;
}

@Component({})
@Component
export default class RegisterPage extends Vue {
  userStore = useUserStore();
  router = useRouter();
  formType = 'company';
  dialogVisible = false;
  errorMessage = '';
  isLoading = false;
  availableCountries: Country[] = [];
  defaultLogoPathWhite = defaultLogoPathWhite;
  companyData: CompanyData = {
    companyName: '',
    username: '',
    email: '',
    vatNumber: '',
    billingAddress: '',
    cekoon_private: false,
    postalCode: '',
    first_name: '',
    last_name: '',
    password: '',
    passwordRepeat: '',
    city: '',
    stateRegion: '',
    country: '',
    phoneNumber: '',
    urlSuffix: this.userStore.domain!.urlSuffix,
    domainId: this.userStore.domain!.domainId.toString(),
  };
  privateData: PrivateData = {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    cekoon_private: true,
    address: '',
    password: '',
    passwordRepeat: '',
    city: '',
    stateRegion: '',
    country: '',
    phoneNumber: '',
    postalCode: '',
    urlSuffix: this.userStore.domain!.urlSuffix,
    domainId: this.userStore.domain!.domainId.toString(),
  };

  async mounted(): Promise<void> {
    await getAllAvailableCountries().then(result =>
      this.availableCountries = [...result].sort((a,b) => (a.name > b.name ? 1 : -1))
    )
  }

  async submitRegister(event: Event | null = null): Promise<void> {
    if (event) event.preventDefault();
    const dataForm = this.$refs.registerForm as InstanceType<typeof ElForm>;
    if (!dataForm) return;
    dataForm.validate(async (valid: boolean) => {
      if (!valid) {
        ElMessage.error(this.$t('error.register.validation'));
        return;
      }
      this.isLoading = true;
      try {
        const formData = new FormData();
        if (this.formType === 'company') {
          formData.append('companyName', this.companyData.companyName);
          formData.append('username', this.companyData.username);
          formData.append('email', this.companyData.email);
          formData.append('vatNumber', this.companyData.vatNumber);
          formData.append('billingAddress', this.companyData.billingAddress);
          formData.append('cekoon_private', this.companyData.cekoon_private ? '1' : '0');
          formData.append('postalCode', this.companyData.postalCode);
          formData.append('first_name', this.companyData.first_name);
          formData.append('last_name', this.companyData.last_name);
          formData.append('password', this.companyData.password);
          formData.append('password_repeat', this.companyData.passwordRepeat);
          formData.append('city', this.companyData.city);
          formData.append('country', this.companyData.country);
          formData.append('phoneNumber', this.companyData.phoneNumber);
          formData.append('stateRegion', this.companyData.country); // temporarily use country for stateRegion
          formData.append('domainId', this.companyData.domainId);
          formData.append('urlSuffix', this.companyData.urlSuffix);          
        } else {
          formData.append('username', this.privateData.username);
          formData.append('email', this.privateData.email);
          formData.append('first_name', this.privateData.first_name);
          formData.append('last_name', this.privateData.last_name);
          formData.append('cekoon_private', this.privateData.cekoon_private ? '1' : '0');
          formData.append('password', this.privateData.password);
          formData.append('password_repeat', this.privateData.passwordRepeat);
          formData.append('city', this.privateData.city);
          formData.append('country', this.privateData.country);
          formData.append('phoneNumber', this.privateData.phoneNumber);
          formData.append('stateRegion', this.privateData.country); // temporarily use country for stateRegion
          formData.append('address', this.privateData.address);
          formData.append('postalCode', this.privateData.postalCode);
          formData.append('domainId', this.privateData.domainId);
          formData.append('urlSuffix', this.privateData.urlSuffix);
        }
        const payload: Record<string, string> = {};
        for (const [key, value] of formData.entries()) {
          payload[key] = value.toString();
        }
        const api = endpointAuthSystem();
        const response = await api.post('api/cekoon/register/authentik', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response) {
          this.router.push({ path: '/login', query: { verify: 'check' } });
          this.dialogVisible = true;
        } else {
          ElMessage.error(this.$t('error.register.invalid'));
        }
      } catch (error: unknown) {
        console.error('Submission failed:', error);
        ElMessage.error(this.$t('error.register.submission'));
      } finally {
        this.isLoading = false;
      }
    });
  }

  navigateTo(path: string): void {
    this.router.push(path);
  }

  handleOk(): void {
    this.dialogVisible = false;
    this.navigateTo('/login');
  }

  // Enforce at least 2 uppercase and 2 lowercase characters, plus a digit and a special char
  validatePassword(rule: FormItemRule, value: string, callback: (error?: Error) => void) {
    const passwordRegex = /^(?=.*[A-Z].*[A-Z])(?=.*[a-z].*[a-z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{10,}$/;
    if (!value) {
      callback(new Error(this.$t('error.register.passwordRequired')));
    } else if (!passwordRegex.test(value)) {
      callback(
        new Error(
          this.$t('error.register.passwordRequirement')));
    } else {
      callback();
    }
  }

  validatePasswordRepeat(rule: FormItemRule, value: string, callback: (error?: Error) => void) {
    if (this.formType === 'company') {
      if (value !== this.companyData.password) {
        callback(new Error(this.$t('error.register.passwordMatch')));
      } else {
        callback();
      }
    } else {
      if (value !== this.privateData.password) {
        callback(new Error(this.$t('error.register.passwordMatch')));
      } else {
        callback();
      }
    }
  }

  validatePhone(rule: FormItemRule, value: string, callback: (err?: Error) => void) {
    const ok = /^\+?[0-9 ()-]{4,20}$/.test(value?.trim() ?? '');
    if (!ok) callback(new Error(this.$t('error.register.phoneInvalid')));
    else callback();
  }
}
</script>

<style scoped>
.wrapper {
  height:100vh;
  position: relative;
  padding: 20px 0 20px;
}
.btn-register {
  color: #fff !important;
}
.error-text {
  color: red;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.4;
}
.form-type-selection {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}
.form-type-selection .el-button {
  flex: 1;
  padding: 10px;
  border: none;
  background-color: white;
  cursor: pointer;
  transition: background-color 0.3s;
}
.form-type-selection .el-button.active {
  border-bottom: 2px solid #000000;
}
.el-form-item {
  margin-bottom: 0px;
}
.flex-column {
  display: flex;
  flex-direction: column;
  gap: 25px;
}
.flex {
  display: flex;
  gap: 32px;
}
.flex-item {
  flex: 1;
  font-size: 14px;
  line-height: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mb-2 {
  margin-bottom: 10px;
}
.button {
  /* margin-top: 48px; */
  width: 100%;
  color: white;
  background-color: #1d1e19;
  border: none;
}
.button:hover {
  background-color: #353535;
}
p {
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
  margin-bottom: 0 !important;
}
span {
  font-size: 14px;
  line-height: 18px;
  color: red;
}
.header {
  width: 100%;
  height: 146px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f0f0;
}
.footer {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  /* height: 71px; */
  gap: 8px;
  padding: 10px 0 0;
}
.content {
  padding: 68px 48px;
}
.dialog {
  font-size: 50px;
  font-weight: 700;
  text-align: center;
  background-color: black;
}
.dialog-container {
  padding: 13%;
}
@media (max-width: 768px) {
  .dialog {
    font-size: 20px;
    font-weight: 700;
  }
}

#logo {
  max-height: 3.4rem;
}

.custom-input::v-deep(.el-input__wrapper) {
  padding: 2px 0px !important;
}
.custom-input::v-deep(.el-input__inner) {
  padding: 0 !important;
  border-radius: 4px !important;
  padding: 1px 11px !important;
}

</style>
